import React, { useState, useEffect } from 'react';
import { Stage, Layer, Rect, Image as KonvaImage, Text } from 'react-konva';
import containerImageSrc from '../../../images/container.png';
import tireImageSrc from '../../../images/pneumatico.png';
import texture1Image from '../../../images/asfalto.png';
import texture2Image from '../../../images/basecourse.png';
import texture3Image from '../../../images/subbase.png';
import texture4Image from '../../../images/subgrade.png';

interface Props {
    layerHeights?: number[]; // Array con altezze dei 4 strati (reso opzionale con default)
    differenziali?: number[]; // Array con i valori differenziali (reso opzionale con default)
    projectType?: 'container_yard' | 'parking_deck' | string;
}

// Valori di default per le props
const defaultLayerHeights = [60, 80, 300, 120];
const defaultDifferenziali = [0, 0, 0, 0];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TireAndLayers: React.FC<Props> = ({ layerHeights = defaultLayerHeights, differenziali = defaultDifferenziali, projectType = 'container_yard' }) => {
    const layersTotalHeight = layerHeights.reduce((acc, h) => acc + h, 0);
    const squareWidth = 400; // Larghezza dei rettangoli
    // --- Image constants and dynamic selection ---
    const IMAGE_TOP_PADDING = 2;
    // Dimensions for Container (container_yard)
    //const CONTAINER_IMAGE_HEIGHT = 100; // Adjusted for typical container aspect
    //const CONTAINER_IMAGE_WIDTH = squareWidth - 150; // Adjusted for typical container aspect
    const CONTAINER_RAPPORTO_DIAGONALE = 1.875; // 690 / 368
    const CONTAINER_IMAGE_HEIGHT = 120;
    const CONTAINER_IMAGE_WIDTH = CONTAINER_IMAGE_HEIGHT * CONTAINER_RAPPORTO_DIAGONALE;

    // Dimensions for Tire (parking_deck)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const TIRE_IMAGE_RADIUS = 65;
    //const TIRE_IMAGE_HEIGHT = TIRE_IMAGE_RADIUS * 2; // originale 350
    //const TIRE_IMAGE_WIDTH = squareWidth - 180; // Adjusted for typical tire aspect
    const TIRE_RAPPORTO_DIAGONALE = 2.2; // 350 / 150
    const TIRE_IMAGE_HEIGHT = 150;
    const TIRE_IMAGE_WIDTH = TIRE_IMAGE_HEIGHT / TIRE_RAPPORTO_DIAGONALE;

    const isParkingDeck = projectType === 'parking_deck';
    const imageSrcToLoad = isParkingDeck ? tireImageSrc : containerImageSrc;
    const currentImageHeight = isParkingDeck ? TIRE_IMAGE_HEIGHT : CONTAINER_IMAGE_HEIGHT;
    const currentImageWidth = isParkingDeck ? TIRE_IMAGE_WIDTH : CONTAINER_IMAGE_WIDTH;
    // --- NUOVE COSTANTI PER IL LAYOUT ---
    const titleAreaWidth = 160; // Spazio dedicato ai titoli a sinistra
    const rectStartX = titleAreaWidth; // Inizio dei rettangoli
    const arrowLength = 60; // Lunghezza della freccia
    const arrowGap = 10; // Spazio tra rettangolo e inizio freccia
    const labelGap = 10; // Spazio tra fine freccia ed etichetta 'd'
    const stageWidth = rectStartX + squareWidth + arrowGap + arrowLength + labelGap + 60; // Larghezza totale calcolata + margine
    const stageHeight = IMAGE_TOP_PADDING + currentImageHeight + layersTotalHeight + 10; /* bottom padding */

    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [textureImg, setTextureImg] = useState<HTMLImageElement | null>(null);
    const [texture2Img, setTexture2Img] = useState<HTMLImageElement | null>(null);
    const [texture3Img, setTexture3Img] = useState<HTMLImageElement | null>(null);
    const [texture4Img, setTexture4Img] = useState<HTMLImageElement | null>(null);

    const titles = ['Surface Layer', 'Base Course', 'Subbase Course', 'Subgrade'];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const strati = ['d1', 'd2', 'd3', 'd4']; // Etichette da aggiungere

    useEffect(() => {
        const img = new window.Image();
        img.src = imageSrcToLoad; // Use dynamic image source
        img.onload = () => setImage(img);
        img.onerror = () => {
            // Intentionally empty - silently ignore image load errors
        };

        const textures = [texture1Image, texture2Image, texture3Image, texture4Image];
        const setters = [setTextureImg, setTexture2Img, setTexture3Img, setTexture4Img];

        textures.forEach((src, index) => {
            const tex = new window.Image();
            tex.src = src;
            tex.onload = () => setters[index](tex);
            tex.onerror = () => {
                // Intentionally empty - silently ignore texture load errors
            };
        });
    }, [imageSrcToLoad]);

    return (
        <Stage width={stageWidth} height={stageHeight}>
            <Layer>
                {/* Disegna i rettangoli con il nuovo layout */}
                {layerHeights.map((height, index) => {
                    // Calcolo Y (parte da sotto l'immagine)
                    const rectY = IMAGE_TOP_PADDING + currentImageHeight + layerHeights.slice(0, index).reduce((acc, h) => acc + h, 0);
                    const centerY = rectY + height / 2;

                    // Calcoli X per il nuovo layout
                    const arrowStartX = rectStartX + squareWidth + arrowGap;
                    const arrowEndX = arrowStartX + arrowLength;
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const labelX = arrowEndX + labelGap;

                    // Determina quale texture usare per questo strato (ordine corretto)
                    let currentTexture: HTMLImageElement | null = null;
                    if (index === 0) currentTexture = textureImg;
                    else if (index === 1) currentTexture = texture2Img;
                    else if (index === 2) currentTexture = texture3Img;
                    else if (index === 3) currentTexture = texture4Img;

                    // Determina la scala della texture
                    let textureScale;
                    if (index === 0) {
                        // Surface Layer (asfalto)
                        textureScale = { x: 0.2, y: 0.2 };
                    } else if (index === 1) {
                        // Base Course
                        textureScale = { x: 0.8, y: 0.8 };
                    } else if (index === 2) {
                        // Subbase Course
                        textureScale = { x: 0.8, y: 0.8 };
                    } else if (index === 3) {
                        // Subgrade
                        textureScale = { x: 0.5, y: 0.5 };
                    }

                    return (
                        <React.Fragment key={index}>
                            {/* Titolo a Sinistra solo se height > 0 */}
                            {height > 0 && <Text text={titles[index]} x={5} y={centerY - 6} width={titleAreaWidth - 10} wrap="word" align="right" fontSize={12} fill="black" />}
                            {/* Rettangolo Strato */}
                            <Rect
                                x={rectStartX}
                                y={rectY}
                                width={squareWidth}
                                height={height}
                                fillPatternImage={currentTexture || undefined}
                                fillPatternRepeat="repeat"
                                fillPatternScale={currentTexture ? textureScale : undefined}
                                stroke="grey"
                                strokeWidth={0.5}
                            />
                        </React.Fragment>
                    );
                })}

                {/* Immagine Pneumatico */}
                {image && (
                    <KonvaImage
                        image={image}
                        x={rectStartX + squareWidth / 2 - currentImageWidth / 2} // Centro esatto sopra il rettangolo
                        y={IMAGE_TOP_PADDING}
                        width={currentImageWidth}
                        height={currentImageHeight}
                    />
                )}
            </Layer>
        </Stage>
    );
};

export default TireAndLayers;
